<template>
  <div>
    <b-container class="py-3">
      <b-row>
        <b-col>

          <page-title :title="pageTitle" />

          <b-alert v-if="client && client.isOffline" show variant="info">
            This client is currently checked out for offline access. You will not be able to perform some actions, like downloading a mitigation report, until they are checked in.
          </b-alert>

          <hr />

          <div v-if="client">
            <b-tabs pills content-class="mt-3">
              <b-tab title="Overview" active>
                <b-card-group>
                  <b-card v-if="client.isOffline">
                    <template v-slot:header>
                      <h2>Client Overview</h2>
                    </template>
                    <b-form-row>
                      <b-col sm="4" class="form-static-label">Name:</b-col>
                      <b-col sm="8" class="form-static-value">{{ client.applicationUser.displayName }}</b-col>
                    </b-form-row>
                    <div class="mt-3">
                      <b-button-group>
                        <b-button variant="primary" :href="client.shortCodeUrl">
                          Proctor Questionnaire
                        </b-button>
                        <b-button :to="'/subscriber/client/' + client.id + '/checkIn'">
                          Check In
                        </b-button>
                      </b-button-group>
                    </div>
                  </b-card>
                  <b-card v-if="!client.isOffline">
                    <template v-slot:header>
                      <h2>Client Overview</h2>
                    </template>
                    <b-form-row>
                      <b-col sm="4" class="form-static-label">Provided Name:</b-col>
                      <b-col sm="8" class="form-static-value">{{getAnswerValue('Pre2', 'ClientName')}}</b-col>
                    </b-form-row>
                    <b-form-row>
                      <b-col sm="4" class="form-static-label">Form Status:</b-col>
                      <b-col sm="8" class="form-static-value">{{defaultForm.formStatus}}</b-col>
                    </b-form-row>
                    <b-form-row>
                      <b-col sm="4" class="form-static-label">Date of Birth:</b-col>
                      <b-col sm="8" class="form-static-value">{{getAnswerValue('Pre2', 'DateOfBirth')}}</b-col>
                    </b-form-row>
                    <b-form-row>
                      <b-col sm="4" class="form-static-label">Gender:</b-col>
                      <b-col sm="8" class="form-static-value">{{getAnswerValue('Pre2', 'Gender')}}</b-col>
                    </b-form-row>
                    <b-form-row>
                      <b-col sm="4" class="form-static-label">Nature of Offense:</b-col>
                      <b-col sm="8" class="form-static-value">{{getAnswerValue('Pre1', 'Offense')}}</b-col>
                    </b-form-row>
                    <b-form-row>
                      <b-col sm="4" class="form-static-label">Link:</b-col>
                      <b-col sm="8" class="form-static-value"><a :href="client.shortCodeUrl" target="_blank">{{client.shortCodeUrl}}</a></b-col>
                    </b-form-row>
                    <div class="mt-3" v-if="client.isProctored && !defaultForm.completed">
                      <b-button-group>
                        <b-button variant="primary" :href="client.shortCodeUrl">
                          Proctor Questionnaire
                        </b-button>
                        <b-button :to="'/subscriber/client/' + client.id + '/checkOut'" v-if="!client.isOffline">
                          Check Out for Offline Access
                        </b-button>
                      </b-button-group>
                    </div>
                    <div class="mt-3">
                      <b-button variant="primary" @click="downloadReport()" :disabled="isDownloading">
                        <b-spinner small v-if="isDownloading"></b-spinner>
                        Download Mitigation Report
                      </b-button>
                    </div>
                  </b-card>
                  <b-card no-body v-if="!client.isOffline">
                    <template v-slot:header>
                      <h2>Timeline</h2>
                    </template>
                    <b-table-simple striped>
                      <b-thead>
                        <b-tr>
                          <b-th>Date/Time</b-th>
                          <b-th>Event</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr>
                          <b-td>{{getFormattedDateTime(defaultForm.created)}}</b-td>
                          <b-td>Invite Sent to Client</b-td>
                        </b-tr>
                        <b-tr v-if="defaultForm.started">
                          <b-td>{{getFormattedDateTime(defaultForm.started)}}</b-td>
                          <b-td>Client Started Survey</b-td>
                        </b-tr>
                        <b-tr v-if="defaultForm.completed">
                          <b-td>{{getFormattedDateTime(defaultForm.completed)}}</b-td>
                          <b-td>Client Completed Survey</b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                    <div class="mt-3 ml-3" v-if="!client.isProctored">
                      <b-button variant="primary" @click="sendReminder()" :disabled="isSendingReminder" v-if="defaultForm.formStatus != 'Completed'">
                        <b-spinner small v-if="isSendingReminder"></b-spinner>
                        Send Reminder Message
                      </b-button>
                    </div>
                  </b-card>
                </b-card-group>
              </b-tab>
              <b-tab title="Reports" v-if="!client.isOffline">
                <p>
                  <span class="float-right">
                    <b-button @click="loadAdjustedForms()"><font-awesome-icon icon="sync"></font-awesome-icon></b-button>
                  </span>
                  AI-generated reports contain minor modifications to your client's mitigation report, correcting mistakes and providing a consistent voice. These
                  reports take several minutes to generate.
                  <b-alert show variant="info">
                    This feature is currently in beta. Please proofread the generated reports.
                  </b-alert>
                </p>
                <b-table-simple>
                  <b-thead>
                    <b-tr>
                      <b-th>Report Variation</b-th>
                      <b-th>Description</b-th>
                      <b-th>Status</b-th>
                      <b-th></b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr>
                      <b-td>Raw</b-td>
                      <b-td>The original mitigation report, with the client's verbatim answers.</b-td>
                      <b-td>Ready</b-td>
                      <b-td>
                        <b-button variant="primary" @click="downloadReport()" :disabled="isDownloading">
                          <b-spinner small v-if="isDownloading"></b-spinner>
                          Download
                        </b-button>
                      </b-td>
                    </b-tr>
                    <b-tr v-for="adjustedForm in adjustedFormsList" v-bind:key="adjustedForm.adjuster">
                      <b-td>{{ adjustedForm.name }}</b-td>
                      <b-td>{{ adjustedForm.description }}</b-td>
                      <b-td v-if="adjustedForm.status == 'Started'">{{ adjustedForm.progress }}</b-td>
                      <b-td v-if="adjustedForm.status == 'Completed'">Ready</b-td>
                      <b-td v-if="!adjustedForm.status"></b-td>
                      <b-td v-if="adjustedForm.status == 'Started'">Please wait...</b-td>
                      <b-td v-if="adjustedForm.status == 'Completed'">
                        <b-button @click="downloadReport2(adjustedForm.id)" :disabled="isDownloading">
                          <b-spinner small v-if="isDownloading"></b-spinner>
                          Download
                        </b-button>
                      </b-td>
                      <b-td v-if="!adjustedForm.status">
                        <b-button @click="adjustForm(adjustedForm)">Generate</b-button>
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </b-tab>
              <b-tab title="Files" v-if="!client.isOffline">
                <div v-if="client">
                  <b-table-simple striped>
                    <b-thead>
                      <b-tr>
                        <b-th>Download</b-th>
                        <b-th>Question</b-th>
                      </b-tr>
                    </b-thead>
                    <b-tbody v-if="!fileAnswers || fileAnswers.length == 0">
                      <b-tr>
                        <b-td colspan="2">{{client.applicationUser.firstName}} {{client.applicationUser.lastName}} has not uploaded any files.</b-td>
                      </b-tr>
                    </b-tbody>
                    <b-tbody v-if="fileAnswers && fileAnswers.length > 0">
                      <b-tr v-for="fileAnswer in fileAnswers" v-bind:key="fileAnswer.key">
                        <b-td><b-link @click="downloadFile(fileAnswer.answerSetId, fileAnswer.filename)">Download</b-link></b-td>
                        <b-td>{{fileAnswer.label}}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>                  
                </div>
              </b-tab>
              <b-tab title="Letters" v-if="!client.isOffline">
                <div v-if="client">
                  <b-table-simple striped>
                    <b-thead>
                      <b-tr>
                        <b-th>Name</b-th>
                        <b-th>Relationship</b-th>
                        <b-th></b-th>
                      </b-tr>
                    </b-thead>
                    <b-tbody v-if="!supporters || supporters.length == 0">
                      <b-tr>
                        <b-td colspan="2">{{client.applicationUser.firstName}} {{client.applicationUser.lastName}} does not have any supporters.</b-td>
                      </b-tr>
                    </b-tbody>
                    <b-tbody v-if="supporters && supporters.length > 0">
                      <b-tr v-for="supporter in supporters" v-bind:key="supporter.id">
                        <b-td>{{supporter.firstName}} {{supporter.lastName}}</b-td>
                        <b-td>{{supporter.clientRelationship}}</b-td>
                        <b-td>
                          <span v-if="supporter.isCompleted">
                            <b-link @click="downloadSupporterLetter(supporter.id)">Download</b-link>
                          </span>
                          <span v-if="!supporter.isCompleted">
                            Incomplete
                          </span>
                        </b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>                  
                </div>
              </b-tab>
              <b-tab title="History" v-if="!client.isOffline">
                <b-table-simple bordered striped>
                  <b-thead>
                    <b-tr>
                      <b-th>Date/Time</b-th>
                      <b-th>Event</b-th>
                      <b-th>Additional Information</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr v-for="clientEvent in orderedClientEvents" :key="clientEvent.id">
                      <b-td>{{getFormattedDateTime(clientEvent.eventTimestamp)}}</b-td>
                      <b-td>{{clientEvent.eventType}}</b-td>
                      <b-td>{{clientEvent.description}}</b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple> 
              </b-tab>
              <b-tab title="Manage" v-if="!client.isOffline">
                <b-card>
                  <template v-slot:header>
                    <h2>Delete</h2>
                  </template>
                  <b-button variant="danger" @click="showDeleteModal">Delete Client</b-button>
                </b-card>
              </b-tab>
            </b-tabs>

          </div>

        </b-col>
      </b-row>
    </b-container>

    <b-modal id="deleteModal" header-bg-variant="dark" header-text-variant="light" title="Delete Client" @ok="deleteClient" ok-variant="danger" ok-title="Delete Client" cancel-title="Do Not Delete Client">
      <p>Once a client is deleted, the following happens:</p>
      <ul>
        <li>The client can no longer submit surveys.</li>
        <li>You will no longer see the client, their survey results, their history, or their mitigation report.</li>
      </ul>
      <p><b>This operation cannot be undone.</b></p>
      <p>Are you sure you want to delete this client?</p>
    </b-modal>

  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import marked from 'marked';
import AuthMixin from '../../Mixins/AuthMixin.vue'

export default {
  name: "SubscriberClientView",
  props: ['id'],
  mixins: [AuthMixin],
  data() {
    return {
      client: null,
      clientEvents: null,
      isDownloading: false,
      isSendingReminder: false,
      adjustedForms: null,
      adjusters: [
        {
          key: 'Default',
          adjuster: 'FirstPersonAdjuster',
          name: 'First Person (Court Ready)',
          description: 'A limited AI-assisted version of the mitigation report to correct spelling and grammatical errors.'
        },
        {
          key: 'Default',
          adjuster: 'ThirdPersonAdjuster',
          name: 'Third Person (Court Ready)',
          description: 'A limited AI-assisted version of the mitigation report to correct spelling and grammatical errors, and to change voice to third person.'
        }
      ]
    }
  },
  computed: {
    defaultForm: function () {
      if (!this.client)
        return null;

      return this.client.forms.find(x => x.key == 'Default' && !x.adjuster);
    },
    pageTitle: function () {
      if (!this.client)
        return 'Loading...';
      
      return this.client.applicationUser.firstName + ' ' + this.client.applicationUser.lastName;
    },
    orderedPages: function () {
      if (!this.client)
        return null;

      return [...this.defaultForm.pages].sort((a, b) => a.displayWeight - b.displayWeight);
    },
    orderedClientEvents: function () {
      if (!this.clientEvents)
        return null;

      return [...this.clientEvents.clientEvents].sort((a, b) => moment(b.eventTimestamp).unix() - moment(a.eventTimestamp).unix());
    },
    supporters: function () {
      if (!this.client)
        return null;

      return this.client.supporters;
    },
    fileAnswers: function () {
      if (!this.client)
        return null;

      let retVal = [];

      this.defaultForm.pages.forEach(page => {
        let questions = page.template.questions.filter(q => q.questionType == 'File');

        if (questions && questions.length > 0) {
          questions.forEach(q => {
            let answer = page.answerSet.answers.find(a => a.key == q.key);
            if (answer && answer.value) {
              retVal.push({
                key: q.key,
                label: q.label,
                answerSetId: page.answerSet.id,
                filename: answer.value
              });
            }
          });
        }
      });

      return retVal;
    },
    adjustedFormsList: function () {
      let retVal = [];

      for (let adjuster of this.adjusters) {
        let item = {
          key: adjuster.key,
          adjuster: adjuster.adjuster,
          name: adjuster.name,
          description: adjuster.description,
        };

        if (this.adjustedForms) {
          let adj = this.adjustedForms.find(x => x.key == item.key && x.adjuster == item.adjuster);

          if (adj) {
            item.id = adj.id;
            item.status = adj.formStatus;
            item.progress = adj.adjusterProgress;
          }
        }

        retVal.push(item);
      }

      return retVal;
    }    
  },
  methods: {
    renderMarkdown: function (value) {
      if (!value)
        return '';

      return marked(value);
    },
    showDeleteModal: function () {
      this.$bvModal.show('deleteModal');
    },
    adjustForm: function (adjustedForm) {
      adjustedForm.status = 'Started';

      axios.post('/api/form/' + this.defaultForm.id + '/adjust',
      {
        adjuster: adjustedForm.adjuster,
        key: adjustedForm.key
      })
      .then(response => {
        this.$parent.showToast('The report is being generated. It will be available here within a few minutes.', {
          title: 'Generating Report',
          variant: 'success',
          autoHideDelay: 5000
        });
        
        this.loadAdjustedForms(true);

      })
      .catch(error => {
        console.log(error);
      });
    },
    deleteClient: function () {
      axios.post('/api/client/deleteClient',
      {
        clientId: this.client.id
      })
      .then(response => {
        this.$parent.showToast('The client has been deleted.', {
          title: 'Saved',
          variant: 'success',
          autoHideDelay: 5000
        });
        
        this.$router.push('/');
      })
      .catch(error => {
        console.log(error);
      });
    },
    getFormattedDateTime: function (datetime) {
      return moment(datetime).format("llll");
    },
    getAnswerValue: function (pageKey, answerKey) {
      if (!this.client)
        return null;

      let page = this.defaultForm.pages.find(x => x.template.key == pageKey);
      if (!page)
        return null;

      let answer = page.answerSet.answers.find(x => x.key == answerKey);

      if (!answer)
        return null;

      return answer.value;
    },
    sendReminder: function () {
      this
        .$bvModal.msgBoxConfirm('Do you want to send a reminder email/SMS message to ' + this.client.applicationUser.firstName + ' ' + this.client.applicationUser.lastName + ' to complete their survey?', { title: 'Send Reminder', okTitle: 'Yes', cancelTitle: 'No', centered: true})
        .then(x => {
          if (x)
            this.sendReminder2();
        });
    },
    sendReminder2: function () {
      this.isSendingReminder = true;

      axios({
        url: '/api/form/' + this.defaultForm.id + '/reminder',
        method: 'POST'
      }).then((response) => {
        this.$parent.showToast('A reminder message was sent to ' + this.client.applicationUser.firstName + ' ' + this.client.applicationUser.lastName + ' to complete their survey.', {
          title: 'Reminder Message Sent',
          variant: 'success',
          autoHideDelay: 5000
        });

        this.isSendingReminder = false;
      });
    },
    downloadReport: function () {
      if (this.defaultForm.formStatus != 'Completed') {
        this
          .$bvModal.msgBoxConfirm(this.client.applicationUser.firstName + ' ' + this.client.applicationUser.lastName + ' has not yet completed their survey. This means their mitigation report is incomplete. Are you sure you want to download it?', { title: 'Incomplete Mitigation Report', okTitle: 'Yes', cancelTitle: 'No', centered: true})
          .then(x => {
            if (x)
              this.downloadReport2(this.defaultForm.id);
          });
      } else {
        this.downloadReport2(this.defaultForm.id);
      }
    },
    downloadReport2: function (formId) {
      this.isDownloading = true;

      let clientName = this.getAnswerValue('Pre2', 'ClientName');
      if (clientName)
        clientName = clientName.replace(/\W/g, '');

      axios({
        url: '/api/account/temporaryAccessToken',
        method: 'GET'
      }).then((response) => {
        window.open('/api/form/' + formId + '/report?temporaryAccessToken=' + response.data.token, '_blank');
        this.isDownloading = false;
      });
    },
    downloadSupporterLetter: function (supporterId) {
      this.isDownloading = true;

      axios({
        url: '/api/account/temporaryAccessToken',
        method: 'GET'
      }).then((response) => {
        window.open('/api/supporter/' + supporterId + '/letter?temporaryAccessToken=' + response.data.token, '_blank');
        this.isDownloading = false;
      });
    },
    downloadFile: function (answerSetId, filename) {
      if (this.defaultForm.formStatus != 'Completed') {
        this
          .$bvModal.msgBoxConfirm(this.client.applicationUser.firstName + ' ' + this.client.applicationUser.lastName + ' has not yet completed their survey. This means any uploaded files may not be complete. Are you sure you want to download the file?', { title: 'Incomplete Mitigation Report', okTitle: 'Yes', cancelTitle: 'No', centered: true})
          .then(x => {
            if (x)
              this.downloadFile2(answerSetId, filename);
          });
      } else {
        this.downloadFile2(answerSetId, filename);
      }
    },
    downloadFile2: function (answerSetId, filename) {
      let clientName = this.getAnswerValue('Pre2', 'ClientName');
      if (clientName)
        clientName = clientName.replace(/\W/g, '');

      axios({
        url: '/api/account/temporaryAccessToken',
        method: 'GET'
      }).then((response) => {
        window.open('/api/answerSet/' + answerSetId + '/file/' + filename + '?temporaryAccessToken=' + response.data.token, '_blank');
        this.isDownloading = false;
      });
    },
    loadAdjustedForms (setTimer) {
      axios
        .get('/api/form?filter=' + encodeURIComponent('originalForm.clientId="' + this.id + '"'))
        .then(response => {
          this.adjustedForms = response.data;

          if (setTimer && this.adjustedForms.some(x => x.formStatus == 'Started')) {
            setTimeout(() => {
              this.loadAdjustedForms(true);
            }, 10000);
          }
        })
        .catch(error => {
          console.log(error);
        });
    },
    load: function () {
      axios
        .get('/api/client/' + this.id + '?includes=applicationUser,forms,forms.pages,forms.pages.answerSet,forms.pages.template,supporters')
        .then(response => {
          this.client = response.data;
        })
        .catch(error => {
          console.log(error);
        });

      // Including clientEvents in the request above leads to very poor performance on clients with a large number of events (it ends up with a huge number of rows due to the LEFT JOINs that EF builds)
      axios
        .get('/api/client/' + this.id + '?includes=clientEvents')
        .then(response => {
          this.clientEvents = response.data;
        })
        .catch(error => {
          console.log(error);
        });

        this.loadAdjustedForms();
    }
  },
  watch: {
  },
  created() {
  },
  async mounted () {
    this.load();
  }
};
</script>